import { ContractRates, Job, JobCurrency } from 'src/shared/api';
import { JobDetailGeneralInformation, JobDetails } from './job-detail.models';
import {
	PrevailingWageInformation,
	PrevailingWageRates,
} from 'src/admin-app/shared/prevailing-wage/prevailing-wage.models';

import { BillingDetails } from 'src/shared/billing-details/billing-details.models';
import { DEFAULT_COUNTRY_CODE } from 'src/shared/constants';
import { LocationDetails } from 'src/shared/job-location/job-location.models';
import { PurchaseOrderDetails } from 'src/admin-app/shared/purchase-order-details/purchase-order-details.models';
import { isEmpty } from 'lodash';
import { jobToOptionalJobFields } from 'src/admin-app/shared/optional-fields/optional-fields.utility';

export function jobFormToJob(formValue: JobDetails): Job {
	let job: Job;
	if (formValue) {
		const {
			contractedRates,
			fieldContacts,
			documents,
			prevailingWageRates,
			prevailingWageInfo,
			...details
		} = formValue;

		job = {
			contractedRates,
			fieldContacts,
			documents,
			prevailingWageInfo: {
				...prevailingWageInfo,
				...prevailingWageRates,
			},
			...details.generalInfo,
			...details.billingDetails,
			...details.purchaseOrderDetails,
			...details.optionalJobFields,
			...details.jobLocation,
		};

		if (isEmpty(job.prevailingWageInfo)) {
			job.prevailingWageInfo = null;
		}
	}
	return job;
}

export function jobToJobForm(job: Job): JobDetails {
	if (job) {
		return {
			generalInfo: jobToGeneralInfo(job),
			jobLocation: jobToJobLocation(job),
			fieldContacts: job.fieldContacts,
			documents: job.documents,
			billingDetails: jobToBillingDetails(job),
			purchaseOrderDetails: jobToPurchaseOrderDetails(job),
			optionalJobFields: jobToOptionalJobFields(job),
			contractedRates: job.contractedRates,
			prevailingWageInfo: jobToPrevailingWageInfo(job),
			prevailingWageRates: jobToPrevailingWageRates(job),
		};
	} else {
		return {
			generalInfo: null,
			jobLocation: null,
			fieldContacts: [],
			billingDetails: null,
			documents: [],
			purchaseOrderDetails: null,
			optionalJobFields: null,
			contractedRates: [],
			prevailingWageRates: null,
			prevailingWageInfo: null,
		};
	}
}

function jobToGeneralInfo(job: Job): JobDetailGeneralInformation {
	return {
		id: job.id,
		number: job.number,
		flairDocsJobId: job.flairDocsJobId,
		customer: job.customer,
		railroad: job.railroad,
		division: job.division,
		subdivision: job.subdivision,
		regularTimeBasis: job.regularTimeBasis,
		overTimeBasis: job.overTimeBasis,
		team: job.team,
		start: job.start,
		end: job.end,
		permitNumber: job.permitNumber,
		quoteStatus: job.quoteStatus,
		isSafetyTrainingComplete: job.isSafetyTrainingComplete,
		descriptionOfSchedule: job.descriptionOfSchedule,
		equipmentDescription: job.equipmentDescription,
		description: job.description,
		notes: job.notes,
		originatingPortalUserEmail: job.originatingPortalUserEmail,
		quoteNotificationRecipients: job.quoteNotificationRecipients,
		dwrNotificationRecipients: job.dwrNotificationRecipients,
		jobSetupFormRefreshDate: job.jobSetupFormRefreshDate,
		status: job.status,
		dateCompleted: job.dateCompleted,
		lastInvoiceCutoffDate: job.lastInvoiceCutoffDate,
		assignments: job.assignments,
		trackSupervisor: job.trackSupervisor,
		unassignedShifts: job.unassignedShifts,
		railroadSubCode: job.railroadSubCode,
		offeredServices: job.offeredServices,
		dateRange: job.dateRange,
		isNonBillable: job.isNonBillable,
		trackID: job.trackID,
		orderNumber: job.orderNumber,
		party: job.party,
		jobType: job.jobType,
		endMarket: job.endMarket,
		daysRequested: job.daysRequested,
	};
}

function jobToJobLocation(job: Job): LocationDetails {
	return {
		latitude: job.latitude,
		longitude: job.longitude,
		startingMilePost: job.startingMilePost,
		endingMilePost: job.endingMilePost,
		distanceFromTracks: job.distanceFromTracks,
		subdivision: job.subdivision,
		location: job.location,
		countryCode: job.countryCode || DEFAULT_COUNTRY_CODE,
		currency: job.currency ?? JobCurrency.Usd,
		state: job.state,
		city: job.city,
	};
}

function jobToBillingDetails(job: Job): BillingDetails {
	return {
		preferredBillingChannel: job.preferredBillingChannel,
		billTo: job.billTo,
		otherBillingCompany: job.otherBillingCompany,
		billingEmails: job.billingEmails,
		billingNotes: job.billingNotes,
		billingAddress: job.billingAddress
	};
}

function jobToPurchaseOrderDetails(job: Job): PurchaseOrderDetails {
	return {
		poNumber: job.poNumber,
		poAmount: job.poAmount,
		alertAmount: job.alertAmount,
		estimatedSpend: job.estimatedSpend,
		alertEmailAddresses: job.alertEmailAddresses,
		alertThresholdPercentage: job.alertThresholdPercentage,
	};
}

function jobToPrevailingWageInfo(job: Job): PrevailingWageInformation {
	const pw = job.prevailingWageInfo;
	if (!pw) {
		return null;
	}
	return {
		id: pw.id,
		projectName: pw.projectName,
		projectNumber: pw.projectNumber,
		city: pw.city,
		county: pw.county,
		state: pw.state,
		certifiedPayrollReportsRequired: pw.certifiedPayrollReportsRequired,
		payrollReportsName: pw.payrollReportsName,
		payrollReportsPhone: pw.payrollReportsPhone,
		payrollReportsEmail: pw.payrollReportsEmail,
		confirmedBy: pw.confirmedBy,
		confirmedDate: pw.confirmedDate,
	};
}

function jobToPrevailingWageRates(job: Job): PrevailingWageRates {
	const pw = job.prevailingWageInfo;
	if (!pw) {
		return null;
	}
	return {
		pw1Rate: pw.pw1Rate,
		pw2Rate: pw.pw2Rate,
		pw3Rate: pw.pw3Rate,
		pw1NightShiftRate: pw.pw1NightShiftRate,
		pw2NightShiftRate: pw.pw2NightShiftRate,
		pw3NightShiftRate: pw.pw3NightShiftRate,
	};
}

export function hasRates(entity: { contractedRates?: ContractRates }): boolean {
	return (
		entity &&
		entity.contractedRates &&
		Object.values(entity.contractedRates).filter((v) => !!v).length > 0
	);
}
